import React, { useEffect } from 'react';
import {
    WaypointTypeEnum,
    TmsStatusEnum,
    TmsRouteTypeEnum,
    TmsBusinessTypeEnum,
    TmsStationSourceEnum,
} from '../../../../enums/TmsEnum';
import { isExternalStationRoute } from '../utils/externalStation';
import { GetAllStationsResponse, TmsStation } from '../graphql/queries/getStationListByBusinessType';
import { GetTmsCarriersByWarehouseIdResponse } from '../graphql/queries/getTmsCarriers';
import { GetHandoverCarriersByWarehouseIdResponse } from '../graphql/queries/getHandoverCarriers';
import { DrawerMode, type StationRow, type WarehouseOption, type RouteFormFilters } from '../types/routeFormTypes';

/** 从站点列表中按 id 查找外部站点 */
const findExternalStationById = (list: TmsStation[], id: number): TmsStation | undefined =>
    list.find(c => c.id != null && c.id === id && c.stationSource === TmsStationSourceEnum.EXTERNAL);

interface UseRouteFormInitParams {
    mode: DrawerMode;
    visible: boolean;
    initialValues: any;
    currentWarehouse: any;
    stationData: GetAllStationsResponse | undefined;
    warehouseOptions: WarehouseOption[];
    carriersData: GetTmsCarriersByWarehouseIdResponse | undefined;
    handoverCarriersData: GetHandoverCarriersByWarehouseIdResponse | undefined;
    filters: RouteFormFilters;
    setFilters: React.Dispatch<React.SetStateAction<RouteFormFilters>>;
    setRouteCode: React.Dispatch<React.SetStateAction<string | undefined>>;
    setHandoverType: React.Dispatch<React.SetStateAction<number | undefined>>;
    setTableData: React.Dispatch<React.SetStateAction<StationRow[]>>;
    initialCarrierIdRef: React.MutableRefObject<string | undefined>;
    initialTableDataRef: React.MutableRefObject<StationRow[]>;
}

export const useRouteFormInit = ({
    mode,
    visible,
    initialValues,
    currentWarehouse,
    stationData,
    warehouseOptions,
    carriersData,
    handoverCarriersData,
    filters,
    setFilters,
    setRouteCode,
    setHandoverType,
    setTableData,
    initialCarrierIdRef,
    initialTableDataRef,
}: UseRouteFormInitParams) => {

    // 初始化表单数据
    useEffect(() => {
        // 编辑模式：不强依赖 warehouseOptions（Temu 三方展示 name 不需要仓库列表），避免偶发"抽屉打开但名称没渲染"
        if (mode === DrawerMode.Edit && initialValues && visible) {
            // 设置筛选项
            const initialCarrierId = initialValues.carrierId?.toString();
            const initialBusinessType = initialValues.businessType ?? TmsBusinessTypeEnum.OwnRoute;
            const isInitialExternalStationRoute = isExternalStationRoute(initialBusinessType, initialValues.routeType);
            const stationCfgList = stationData?.getAllStations ?? [];
            setFilters({
                routeType: initialValues.routeType,
                businessType: initialBusinessType,
                carrierId: initialCarrierId,
                status: initialValues.status
            });

            // 初始化routeCode
            setRouteCode(initialValues.routeCode ?? undefined);

            // 保存初始承运商ID
            initialCarrierIdRef.current = initialCarrierId;

            // 从途经点构建表格数据
            if (initialValues.waypoints && Array.isArray(initialValues.waypoints) && initialValues.waypoints.length > 0) {
                // 按 waypointOrder 排序
                const sortedWaypoints = [...initialValues.waypoints].sort((a: any, b: any) => a.waypointOrder - b.waypointOrder);

                const newTableData: StationRow[] = [];

                // 根据 id 查找仓库 sortingCode
                const getSortingCodeById = (warehouseId: number | string): string => {
                    if (!warehouseOptions.length) return '';
                    const warehouse = warehouseOptions.find(w => Number(w.id) === Number(warehouseId));
                    return warehouse?.sortingCode || '';
                };

                // 按顺序处理途经点
                for (let i = 0; i < sortedWaypoints.length; i++) {
                    const waypoint = sortedWaypoints[i];
                    const isOrigin = waypoint.waypointType === WaypointTypeEnum.Origin;

                    // 解析站点名称
                    let station = waypoint.waypointSortingCode?.trim() || '';
                    if (!station) {
                        if (isInitialExternalStationRoute && !isOrigin) {
                            const extId = waypoint.extWaypointId;
                            station = (extId != null
                                ? findExternalStationById(stationCfgList, extId)?.name
                                : undefined
                            )?.trim() || '';
                        } else {
                            station = getSortingCodeById(waypoint.waypointId);
                        }
                    }

                    // 确定 key
                    const key = isOrigin ? WaypointTypeEnum.Origin
                        : waypoint.waypointType === WaypointTypeEnum.Destination ? WaypointTypeEnum.Destination
                        : `${WaypointTypeEnum.Waypoint}-${waypoint.id}`;

                    newTableData.push({
                        key,
                        type: waypoint.waypointType,
                        station,
                        // 始发站无行驶时间和距离
                        travelTime: isOrigin ? 0 : (waypoint.travelTime ?? 0),
                        dwellTime: waypoint.dwellTime ?? 0,
                        distance: isOrigin ? 0 : (waypoint.distance ?? 0),
                        // 始发站不存 ext_waypoint_id
                        ...(!isOrigin && waypoint.extWaypointId != null
                            ? { extWaypointId: waypoint.extWaypointId as number }
                            : {}),
                    });
                }

                // 确保始发站和终点存在
                if (!newTableData.some(row => row.type === WaypointTypeEnum.Origin)) {
                    newTableData.unshift({
                        key: WaypointTypeEnum.Origin,
                        type: WaypointTypeEnum.Origin,
                        station: currentWarehouse?.sortingCode || '',
                        travelTime: 0,
                        dwellTime: 0,
                        distance: 0
                    });
                }
                if (!newTableData.some(row => row.type === WaypointTypeEnum.Destination)) {
                    newTableData.push({
                        key: WaypointTypeEnum.Destination,
                        type: WaypointTypeEnum.Destination,
                        station: '',
                        travelTime: 0,
                        dwellTime: 0,
                        distance: 0
                    });
                }

                setTableData(newTableData);
                // 保存初始表格数据（深拷贝）
                initialTableDataRef.current = JSON.parse(JSON.stringify(newTableData));
            } else {
                // 无途经点，使用默认结构
                const defaultTableData = [
                    { key: WaypointTypeEnum.Origin, type: WaypointTypeEnum.Origin, station: currentWarehouse?.sortingCode || '', travelTime: 0, dwellTime: 0, distance: 0 },
                    { key: WaypointTypeEnum.Destination, type: WaypointTypeEnum.Destination, station: '', travelTime: 0, dwellTime: 0, distance: 0 }
                ];
                setTableData(defaultTableData);
                // 保存初始表格数据（深拷贝）
                initialTableDataRef.current = JSON.parse(JSON.stringify(defaultTableData));
            }
        } else if (mode === DrawerMode.Create && visible) {
            // 创建模式重置（状态默认为 Active）
            setFilters({
                routeType: undefined as number | undefined,
                businessType: TmsBusinessTypeEnum.OwnRoute as number | undefined,
                carrierId: undefined as string | undefined,
                status: TmsStatusEnum.Active as number | undefined
            });
            setTableData([
                { key: WaypointTypeEnum.Origin, type: WaypointTypeEnum.Origin, station: currentWarehouse?.sortingCode || '', travelTime: 0, dwellTime: 0, distance: 0 },
                { key: WaypointTypeEnum.Destination, type: WaypointTypeEnum.Destination, station: '', travelTime: 0, dwellTime: 0, distance: 0 }
            ]);
            setRouteCode(undefined);
            setHandoverType(undefined);
        }
        // 注意：不要把 stationData / warehouseOptions 放进依赖。切换业务类型会触发重新查询导致 warehouseOptions 变化，
        // 若依赖该项会再次进入 create 分支并重置 filters（业务类型被刷回自有），表现为切换不了业务类型。
    }, [mode, initialValues, visible, currentWarehouse]);

    // 编辑模式下，承运商/交接方列表加载后同步关联字段
    useEffect(() => {
        if (mode !== DrawerMode.Edit || !visible || !filters.carrierId) {
            return;
        }

        // 交接类型：从交接方列表同步 handoverType
        if (filters.routeType === TmsRouteTypeEnum.Handover) {
            const handoverCarriers = handoverCarriersData?.getHandoverCarriersByWarehouseId;
            if (!handoverCarriers?.length) return;
            const carrier = handoverCarriers.find(c => c.id === filters.carrierId);
            if (!carrier) return;
            setHandoverType(prev => prev === carrier.carrierType ? prev : carrier.carrierType);
            return;
        }

        // carrierSourceType 已在交接方需求中废弃，非交接类型不再同步
    }, [mode, visible, filters.routeType, filters.businessType, filters.carrierId, carriersData?.getTmsCarriersByWarehouseId, handoverCarriersData?.getHandoverCarriersByWarehouseId]);
};
